import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Field, SelectField } from "./DealsAdmin";

const EMPTY = {
  title: "", slug: "", format: "landscape", provider: "youtube",
  is_vlog: true, is_featured: false,
  host: "JetsetLess", guests: [], location: "",
  episode_number: "", season: 1,
  duration_seconds: "",
  video_url: "", thumbnail: "", featured_image: "",
  description: "", script_text: "", transcript: "",
  chapters: [], tags: [],
  status: "draft", published_at: "",
};

export default function VideosAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/videos?limit=200").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...editing };
      ["episode_number", "season", "duration_seconds"].forEach((k) => {
        if (body[k] === "" || body[k] === null) body[k] = null;
        else body[k] = Number(body[k]);
      });
      if (editing.id) await api.put(`/videos/${editing.id}`, body);
      else await api.post("/videos", body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/videos/${id}`); load(); };

  const asList = (v) => Array.isArray(v) ? v.join(", ") : (v || "");
  const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="p-8 md:p-10">
      <div className="flex justify-between mb-6">
        <div>
          <div className="overline text-[#B4533C]">Videos</div>
          <h1 className="font-serif text-4xl mt-1">Video Blog & Shorts</h1>
          <p className="text-sm text-[#4A4742] mt-1">Vlog episodes (long-form) and shorts (vertical clips) share this queue.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="bg-[#1B3B36] text-white px-5 py-2.5 text-sm" data-testid="new-video-btn">+ New Episode</button>
      </div>

      <div className="border border-[#D6CFC4]">
        <table className="w-full admin-table">
          <thead className="bg-[#EFECE5]"><tr>
            <th className="p-3 text-left">Title</th><th className="p-3 text-left">Kind</th><th className="p-3 text-left">Ep</th><th className="p-3 text-left">Duration</th><th className="p-3 text-left">Status</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} className="border-t border-[#D6CFC4]" data-testid={`video-row-${v.id}`}>
                <td className="p-3"><button onClick={() => setEditing(v)} className="hover:text-[#B4533C] text-left">{v.title}</button></td>
                <td className="p-3 text-xs font-mono">{v.is_vlog ? "vlog" : "short"}</td>
                <td className="p-3 text-xs font-mono">{v.episode_number ? `S${v.season}E${v.episode_number}` : "—"}</td>
                <td className="p-3 text-xs font-mono">{v.duration_seconds ? `${Math.floor(v.duration_seconds/60)}:${String(v.duration_seconds%60).padStart(2,'0')}` : "—"}</td>
                <td className="p-3 text-xs font-mono">{v.status}</td>
                <td className="p-3 text-right"><button onClick={() => del(v.id)} className="text-[#B4533C] text-xs">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-6 overflow-y-auto">
          <div className="bg-white border border-[#D6CFC4] p-8 w-full max-w-4xl max-h-[92vh] overflow-y-auto">
            <h2 className="font-serif text-2xl mb-4">{editing.id ? "Edit" : "New"} Video</h2>

            <div className="flex gap-4 mb-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_vlog} onChange={(e) => setEditing({ ...editing, is_vlog: e.target.checked })} /> Vlog (long-form episode)</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} /> Featured on /videos</label>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Title" v={editing.title} k="title" e={setEditing} />
              <Field label="Slug" v={editing.slug} k="slug" e={setEditing} />
              <SelectField label="Format" v={editing.format} k="format" e={setEditing} opts={["landscape","vertical"]} />
              <SelectField label="Provider" v={editing.provider} k="provider" e={setEditing} opts={["youtube","vimeo","manual"]} />
              <Field label="Video URL (YouTube/Vimeo)" v={editing.video_url} k="video_url" e={setEditing} />
              <Field label="Thumbnail URL" v={editing.thumbnail} k="thumbnail" e={setEditing} />
              <Field label="Featured image URL" v={editing.featured_image} k="featured_image" e={setEditing} />
              <Field label="Host" v={editing.host} k="host" e={setEditing} />
              <Field label="Location" v={editing.location} k="location" e={setEditing} />
              <Field label="Season" v={editing.season} k="season" e={setEditing} type="number" />
              <Field label="Episode #" v={editing.episode_number} k="episode_number" e={setEditing} type="number" />
              <Field label="Duration (seconds)" v={editing.duration_seconds} k="duration_seconds" e={setEditing} type="number" />
              <SelectField label="Status" v={editing.status} k="status" e={setEditing} opts={["draft","ready","published"]} />
              <Field label="Publish date (ISO)" v={editing.published_at} k="published_at" e={setEditing} />
            </div>

            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="overline">Guests (comma)</label>
                <input value={asList(editing.guests)} onChange={(e) => setEditing({ ...editing, guests: toList(e.target.value) })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" />
              </div>
              <div>
                <label className="overline">Tags (comma)</label>
                <input value={asList(editing.tags)} onChange={(e) => setEditing({ ...editing, tags: toList(e.target.value) })} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" />
              </div>
            </div>

            <div className="mt-3">
              <label className="overline">Description / show notes (HTML)</label>
              <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={6} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm font-mono" data-testid="video-description-input" />
            </div>
            <div className="mt-3">
              <label className="overline">Script (for shorts)</label>
              <textarea value={editing.script_text} onChange={(e) => setEditing({ ...editing, script_text: e.target.value })} rows={4} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm font-mono" />
            </div>
            <div className="mt-3">
              <label className="overline">Transcript</label>
              <textarea value={editing.transcript} onChange={(e) => setEditing({ ...editing, transcript: e.target.value })} rows={6} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm font-mono" />
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Chapters</label>
                <button onClick={() => setEditing({ ...editing, chapters: [...(editing.chapters || []), { t: "", title: "" }] })} className="text-xs border border-[#D6CFC4] px-3 py-1">+ Add chapter</button>
              </div>
              {(editing.chapters || []).map((c, i) => (
                <div key={i} className="grid grid-cols-6 gap-2 mb-2">
                  <input value={c.t || ""} onChange={(e) => { const ch = [...editing.chapters]; ch[i] = { ...ch[i], t: e.target.value }; setEditing({ ...editing, chapters: ch }); }} placeholder="00:45" className="col-span-1 border border-[#D6CFC4] px-2 py-1.5 text-sm font-mono" />
                  <input value={c.title || ""} onChange={(e) => { const ch = [...editing.chapters]; ch[i] = { ...ch[i], title: e.target.value }; setEditing({ ...editing, chapters: ch }); }} placeholder="Chapter title" className="col-span-4 border border-[#D6CFC4] px-2 py-1.5 text-sm" />
                  <button onClick={() => setEditing({ ...editing, chapters: editing.chapters.filter((_, j) => j !== i) })} className="col-span-1 text-xs text-[#B4533C]">Remove</button>
                </div>
              ))}
            </div>

            <div className="mt-5 flex gap-3">
              <button onClick={save} className="bg-[#1B3B36] text-white px-5 py-2.5 text-sm" data-testid="save-video-btn">Save</button>
              <button onClick={() => setEditing(null)} className="border border-[#D6CFC4] px-5 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
